import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Game.scss';
import Player from './Player';
import Platform from './Platform';
import Fireball from './Fireball';
import Star from './Star';
import GameOver from './GameOver';
import Countdown from './Countdown';

const Game = () => {
  const canvasWidth = 1280;
  const canvasHeight = 720;
  const platformWidth = 150;
  const platformHeight = 96;
  const starCount = 100;
  const fireballSize = 25;
  const playerImageSize = 125;

  const initialGameState = {
    player: {
      x: 100,
      y: canvasHeight - platformHeight - 130,
      width: playerImageSize * 0.7,
      height: playerImageSize * 0.85,
      velocityX: 0,
      velocityY: 0,
      isJumping: false,
      isDashing: false,
      dashCount: 0,
      direction: 'right',
    },
    platforms: [{ x: 100, y: canvasHeight - platformHeight, width: platformWidth, height: platformHeight }],
    stars: Array.from({ length: starCount }, () => ({
      x: Math.random() * canvasWidth,
      y: Math.random() * canvasHeight,
      size: Math.random() * 3 + 1,
    })),
    fireballs: [],
    score: 0,
    gameOver: false,
    gameStarted: false,
  };

  const gameStateRef = useRef(initialGameState);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const keysRef = useRef({ w: false, a: false, s: false, d: false, ' ': false });
  const canvasRef = useRef(null);
  const gamepadIndexRef = useRef(null);

  const gameConstantsRef = useRef({
    gravity: 0.5,
    jumpForce: -20,
    dashForce: 25,
    moveSpeed: 0.5,
    maxSpeed: 10,
    friction: 0.95,
    dashDuration: 8,
    maxDashes: 4,
    platformSpeed: 2,
    minPlatformDistance: 125,
    maxPlatformDistance: 250,
    fireballSpeed: 3,
  });

  const platformImageRef = useRef(null);
  const lavaImagesRef = useRef([]);
  const fireballImagesRef = useRef([]);
  const fireballFrameIndex = useRef(0);
  const fireballFrameCounter = useRef(0);
  const lavaFrameIndex = useRef(0);
  const lavaFrameCounter = useRef(0);
  const gameOverImageRef = useRef(null);
  const fireballSpawnIntervalRef = useRef(null);

  const playerImagesRef = useRef({
    standing: null,
    jumping: null,
    runningStep1: null,
    runningStep2: null,
    dash: null,
  });

  const handleGamepadConnected = (event) => {
    gamepadIndexRef.current = event.gamepad.index;
    console.log('Gamepad connected:', event.gamepad);
  };

  const handleGamepadDisconnected = () => {
    gamepadIndexRef.current = null;
    console.log('Gamepad disconnected');
  };

  const spawnFireball = useCallback(() => {
    if (gameStateRef.current.gameOver) return;

    const newFireball = {
      x: Math.random() * canvasWidth,
      y: canvasHeight - 25 - fireballSize,
      size: fireballSize,
      frame: 0,
    };
    gameStateRef.current.fireballs.push(newFireball);
    console.log('Spawned Fireball:', newFireball); // Debugging log
    console.log('Fireballs Array:', gameStateRef.current.fireballs); // Debugging log
  }, [canvasWidth, canvasHeight, fireballSize]);

  const updateGameState = useCallback(() => {
    if (gameStateRef.current.gameOver || !gameStateRef.current.gameStarted) return;

    const { player, platforms, stars, fireballs } = gameStateRef.current;
    const {
      gravity,
      jumpForce,
      dashForce,
      moveSpeed,
      maxSpeed,
      friction,
      dashDuration,
      maxDashes,
      platformSpeed,
      fireballSpeed,
    } = gameConstantsRef.current;

    let newX = player.x;
    let newY = player.y;
    let newVelocityX = player.velocityX;
    let newVelocityY = player.velocityY;

    if (gamepadIndexRef.current !== null) {
      const gamepad = navigator.getGamepads()[gamepadIndexRef.current];
      if (gamepad) {
        keysRef.current.w = gamepad.buttons[12].pressed;
        keysRef.current.a = gamepad.buttons[14].pressed;
        keysRef.current.s = gamepad.buttons[13].pressed;
        keysRef.current.d = gamepad.buttons[15].pressed;
        keysRef.current[' '] = gamepad.buttons[0].pressed;

        if (gamepad.axes[0] < -0.5) keysRef.current.a = true;
        if (gamepad.axes[0] > 0.5) keysRef.current.d = true;
        if (gamepad.axes[1] < -0.5) keysRef.current.w = true;
        if (gamepad.axes[1] > 0.5) keysRef.current.s = true;
      }
    }

    if (keysRef.current.a) {
      newVelocityX -= moveSpeed;
      player.direction = 'left';
    }
    if (keysRef.current.d) {
      newVelocityX += moveSpeed;
      player.direction = 'right';
    }

    if (keysRef.current.w && !player.isJumping) {
      newVelocityY = jumpForce;
      player.isJumping = true;
    }

    if (keysRef.current[' '] && !player.isDashing && player.dashCount < maxDashes) {
      player.isDashing = true;
      player.dashFrames = dashDuration;
      player.dashCount++;
      const dashDirectionX = (keysRef.current.d ? 1 : 0) - (keysRef.current.a ? 1 : 0);
      const dashDirectionY = (keysRef.current.s ? 1 : 0) - (keysRef.current.w ? 1 : 0);

      if (dashDirectionX !== 0 || dashDirectionY !== 0) {
        const dashMagnitude = Math.sqrt(dashDirectionX ** 2 + dashDirectionY ** 2);
        newVelocityX = (dashDirectionX / dashMagnitude) * dashForce;
        newVelocityY = (dashDirectionY / dashMagnitude) * dashForce;
      } else {
        newVelocityX = player.velocityX > 0 ? dashForce : -dashForce;
      }
    }

    if (player.isDashing) {
      player.dashFrames--;
      if (player.dashFrames <= 0) {
        player.isDashing = false;
      }
    } else {
      newVelocityX *= friction;
      newVelocityY *= friction;
    }

    const speed = Math.sqrt(newVelocityX ** 2 + newVelocityY ** 2);
    if (speed > maxSpeed) {
      const ratio = maxSpeed / speed;
      newVelocityX *= ratio;
      newVelocityY *= ratio;
    }

    newVelocityY += gravity;

    newX += newVelocityX;
    newY += newVelocityY;

    let onPlatform = false;
    platforms.forEach(platform => {
      const playerBottom = newY + player.height;
      const platformTop = platform.y;
      const playerRight = newX + player.width;
      const platformLeft = platform.x;
      const playerLeft = newX;
      const platformRight = platform.x + platform.width;

      if (
        playerRight > platformLeft &&
        playerLeft < platformRight &&
        playerBottom > platformTop &&
        playerBottom - newVelocityY <= platformTop
      ) {
        newY = platform.y - player.height;
        newVelocityY = 0;
        player.isJumping = false;
        player.dashCount = 0;
        onPlatform = true;
      }

      platform.x -= platformSpeed;
    });

    if (!onPlatform && newY > canvasHeight - player.height) {
      gameStateRef.current.gameOver = true;
      setGameOver(true);
      return;
    }

    if (newX < 0) {
      newX = 0;
      newVelocityX = 0;
    } else if (newX > canvasWidth - player.width) {
      newX = canvasWidth - player.width;
      newVelocityX = 0;
    }

    stars.forEach(star => {
      star.x += platformSpeed;
      if (star.x > canvasWidth) {
        star.x = 0;
        star.y = Math.random() * canvasHeight;
        star.size = Math.random() * 3 + 1;
      }
    });

    console.log('Fireballs before update:', fireballs); // Debugging log

    fireballs.forEach((fireball, index) => {
      fireball.y -= fireballSpeed;
      if (fireball.y + fireball.size < 0) {
        fireballs.splice(index, 1);
      }

      console.log(`Fireball Position: x=${fireball.x}, y=${fireball.y}, size=${fireball.size}`); // Debugging log

      if (
        player.x < fireball.x + fireball.size &&
        player.x + player.width > fireball.x &&
        player.y < fireball.y + fireball.size &&
        player.y + player.height > fireball.y
      ) {
        gameStateRef.current.gameOver = true;
        setGameOver(true);
        return;
      }
    });

    console.log('Fireballs after update:', fireballs); // Debugging log

    const remainingPlatforms = platforms.filter(platform => platform.x + platform.width > 0);
    const lastPlatform = remainingPlatforms[remainingPlatforms.length - 1];
    if (lastPlatform && lastPlatform.x < canvasWidth - gameConstantsRef.current.maxPlatformDistance) {
      const platformDistance = Math.random() * (gameConstantsRef.current.maxPlatformDistance - gameConstantsRef.current.minPlatformDistance) + gameConstantsRef.current.minPlatformDistance;
      const newPlatformHeight = platformHeight + Math.random() * 50;
      const newPlatform = {
        x: canvasWidth + platformDistance,
        y: canvasHeight - newPlatformHeight,
        width: platformWidth,
        height: newPlatformHeight,
      };
      remainingPlatforms.push(newPlatform);
    }

    gameStateRef.current = {
      ...gameStateRef.current,
      player: {
        ...player,
        x: newX,
        y: newY,
        velocityX: newVelocityX,
        velocityY: newVelocityY,
      },
      platforms: remainingPlatforms,
      stars,
      fireballs,
      score: gameStateRef.current.score + 1,
    };

    setScore(gameStateRef.current.score);
  }, [canvasWidth, canvasHeight]);

  const handleRetry = () => {
    gameStateRef.current = {
      ...initialGameState,
      stars: Array.from({ length: starCount }, () => ({
        x: Math.random() * canvasWidth,
        y: Math.random() * canvasHeight,
        size: Math.random() * 3 + 1,
      })),
    };
    setScore(0);
    setGameOver(false);
    setCountdown(3);
  };

  useEffect(() => {
    const platformImage = new Image();
    platformImage.src = `${process.env.PUBLIC_URL}/platform.png`;
    platformImage.onload = () => {
      console.log('platform.png loaded successfully');
      platformImageRef.current = platformImage;
      if (lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const lavaImage1 = new Image();
    lavaImage1.src = `${process.env.PUBLIC_URL}/lava_1.png`;
    lavaImage1.onload = () => {
      console.log('lava_1.png loaded successfully');
      lavaImagesRef.current[0] = lavaImage1;
      if (lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const lavaImage2 = new Image();
    lavaImage2.src = `${process.env.PUBLIC_URL}/lava_2.png`;
    lavaImage2.onload = () => {
      console.log('lava_2.png loaded successfully');
      lavaImagesRef.current[1] = lavaImage2;
      if (lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const lavaImage3 = new Image();
    lavaImage3.src = `${process.env.PUBLIC_URL}/lava_3.png`;
    lavaImage3.onload = () => {
      console.log('lava_3.png loaded successfully');
      lavaImagesRef.current[2] = lavaImage3;
      if (lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const playerStanding = new Image();
    playerStanding.src = `${process.env.PUBLIC_URL}/deruptar_standing.png`;
    playerStanding.onload = () => {
      console.log('deruptar_standing.png loaded successfully');
      playerImagesRef.current.standing = playerStanding;
      if (lavaImagesRef.current.length === 3 && platformImageRef.current && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const playerJumping = new Image();
    playerJumping.src = `${process.env.PUBLIC_URL}/deruptar_jumping.png`;
    playerJumping.onload = () => {
      console.log('deruptar_jumping.png loaded successfully');
      playerImagesRef.current.jumping = playerJumping;
      if (lavaImagesRef.current.length === 3 && platformImageRef.current && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const playerRunningStep1 = new Image();
    playerRunningStep1.src = `${process.env.PUBLIC_URL}/deruptar_running_step1.png`;
    playerRunningStep1.onload = () => {
      console.log('deruptar_running_step1.png loaded successfully');
      playerImagesRef.current.runningStep1 = playerRunningStep1;
      if (lavaImagesRef.current.length === 3 && platformImageRef.current && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const playerRunningStep2 = new Image();
    playerRunningStep2.src = `${process.env.PUBLIC_URL}/deruptar_running_step2.png`;
    playerRunningStep2.onload = () => {
      console.log('deruptar_running_step2.png loaded successfully');
      playerImagesRef.current.runningStep2 = playerRunningStep2;
      if (lavaImagesRef.current.length === 3 && platformImageRef.current && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const playerDash = new Image();
    playerDash.src = `${process.env.PUBLIC_URL}/deruptar_dash.png`;
    playerDash.onload = () => {
      console.log('deruptar_dash.png loaded successfully');
      playerImagesRef.current.dash = playerDash;
      if (lavaImagesRef.current.length === 3 && platformImageRef.current && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    const fireballImage1 = new Image();
    fireballImage1.src = `${process.env.PUBLIC_URL}/fire_ball1.png`;
    fireballImage1.onload = () => {
      console.log('fire_ball1.png loaded successfully');
      fireballImagesRef.current[0] = fireballImage1;
      if (fireballImagesRef.current.length === 3 && lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && platformImageRef.current) setImagesLoaded(true);
    };

    const fireballImage2 = new Image();
    fireballImage2.src = `${process.env.PUBLIC_URL}/fire_ball2.png`;
    fireballImage2.onload = () => {
      console.log('fire_ball2.png loaded successfully');
      fireballImagesRef.current[1] = fireballImage2;
      if (fireballImagesRef.current.length === 3 && lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && platformImageRef.current) setImagesLoaded(true);
    };

    const fireballImage3 = new Image();
    fireballImage3.src = `${process.env.PUBLIC_URL}/fire_ball3.png`;
    fireballImage3.onload = () => {
      console.log('fire_ball3.png loaded successfully');
      fireballImagesRef.current[2] = fireballImage3;
      if (fireballImagesRef.current.length === 3 && lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && platformImageRef.current) setImagesLoaded(true);
    };

    const gameOverImage = new Image();
    gameOverImage.src = `${process.env.PUBLIC_URL}/game_over.png`;
    gameOverImage.onload = () => {
      console.log('game_over.png loaded successfully');
      gameOverImageRef.current = gameOverImage;
      if (lavaImagesRef.current.length === 3 && playerImagesRef.current.standing && platformImageRef.current && fireballImagesRef.current.length === 3) setImagesLoaded(true);
    };

    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      gameStateRef.current.gameStarted = true;
      const initialPlatform = {
        x: 500,
        y: canvasHeight - platformHeight,
        width: platformWidth,
        height: platformHeight,
      };
      gameStateRef.current.platforms.push(initialPlatform);
      console.log('Initial platform:', initialPlatform);
    }
  }, [countdown]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      keysRef.current[e.key.toLowerCase()] = true;
    };

    const handleKeyUp = (e) => {
      keysRef.current[e.key.toLowerCase()] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
    };
  }, []);

  useEffect(() => {
    if (!gameOver && gameStateRef.current.gameStarted) {
      const interval = setInterval(() => {
        spawnFireball();
      }, Math.random() * 4000 + 1000); // Random interval between 1-5 seconds
      fireballSpawnIntervalRef.current = interval;
      return () => clearInterval(interval);
    }
  }, [gameOver, gameStateRef.current.gameStarted, spawnFireball]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const gameLoop = () => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Update game state
      updateGameState();

      // Draw the background
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Draw the stars
      ctx.fillStyle = 'white';
      const { stars } = gameStateRef.current;
      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw the player
      const { player, platforms, fireballs } = gameStateRef.current;
      let playerImage = playerImagesRef.current.standing;

      if (player.isDashing) {
        playerImage = playerImagesRef.current.dash;
      } else if (player.isJumping) {
        playerImage = playerImagesRef.current.jumping;
      } else if (keysRef.current.a || keysRef.current.d) {
        if (Math.floor(gameStateRef.current.score / 10) % 2 === 0) {
          playerImage = playerImagesRef.current.runningStep1;
        } else {
          playerImage = playerImagesRef.current.runningStep2;
        }
      }

      if (playerImage) {
        if (player.direction === 'left') {
          ctx.save();
          ctx.scale(-1, 1);
          ctx.drawImage(playerImage, -player.x - playerImageSize, player.y, playerImageSize, playerImageSize);
          ctx.restore();
        } else {
          ctx.drawImage(playerImage, player.x, player.y, playerImageSize, playerImageSize);
        }
      }

      // Draw the platforms if the image is loaded
      if (imagesLoaded) {
        platforms.forEach(platform => {
          ctx.drawImage(platformImageRef.current, platform.x, platform.y, platform.width, platform.height);
        });

        // Draw the fireballs
        fireballs.forEach((fireball, index) => {
          console.log('Drawing Fireball:', fireball); // Debugging log
          if (fireballImagesRef.current.length === 3) {
            if (fireballFrameCounter.current % 10 === 0) {
              fireballFrameIndex.current = (fireballFrameIndex.current + 1) % fireballImagesRef.current.length;
            }
            ctx.drawImage(fireballImagesRef.current[fireballFrameIndex.current], fireball.x, fireball.y, fireball.size, fireball.size);
          }
        });

        // Draw the lava
        if (lavaFrameCounter.current % 10 === 0) {
          lavaFrameIndex.current = (lavaFrameIndex.current + 1) % lavaImagesRef.current.length;
        }
        ctx.drawImage(lavaImagesRef.current[lavaFrameIndex.current], 0, canvasHeight - 25, canvasWidth, 25);
        lavaFrameCounter.current++;
      }

      // Draw game over image if the game is over
      if (gameOver) {
        const gameOverImage = gameOverImageRef.current;
        if (gameOverImage) {
          const x = (canvasWidth - gameOverImage.width) / 2;
          const y = (canvasHeight - gameOverImage.height) / 2;
          ctx.drawImage(gameOverImage, x, y);
        }
      }

      // Request next frame
      animationFrameId = requestAnimationFrame(gameLoop);
    };

    if (imagesLoaded) {
      gameLoop();
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [updateGameState, canvasWidth, canvasHeight, imagesLoaded, gameOver]);

  useEffect(() => {
    if (gameOver) {
      clearInterval(fireballSpawnIntervalRef.current);
    }
  }, [gameOver]);

  return (
    <div className="Game">
      <canvas className='game-canvas' ref={canvasRef} width={canvasWidth} height={canvasHeight} />
      <div className='score'>Score: {score}</div>
      {countdown > 0 && <Countdown countdown={countdown} className='countdown' />}
      {gameOver && <GameOver handleRetry={handleRetry} className='game-over' />}
    </div>
  );
};

export default Game;
